import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Server, Plus, Trash2, Play, Pause } from 'lucide-react'
import { devicesApi, tunnelsApi, relaysApi } from '../api/client'
import type { Device, DeviceInstallation, Tunnel } from '../types'
import { formatDistanceToNow } from 'date-fns'
import clsx from 'clsx'
import { useAuth } from '../context/AuthContext'

interface Relay {
  id: string
  name: string
  address: string
  is_default: boolean
  status: string
}

function TunnelStatusBadge({ status }: { status: Tunnel['status'] }) {
  const config = {
    ready: { color: 'bg-green-500/10 text-green-400', label: 'Ready' },
    connecting: { color: 'bg-yellow-500/10 text-yellow-400', label: 'Connecting' },
    pending: { color: 'bg-blue-500/10 text-blue-400', label: 'Pending' },
    error: { color: 'bg-red-500/10 text-red-400', label: 'Error' },
    disconnected: { color: 'bg-slate-500/10 text-slate-400', label: 'Disconnected' },
  }
  const { color, label } = config[status]

  return (
    <span className={clsx('px-2 py-1 text-xs font-medium rounded', color)}>
      {label}
    </span>
  )
}

function InstallationStatusBadge({ status }: { status: DeviceInstallation['status'] }) {
  const config = {
    approved: { color: 'bg-blue-500/10 text-blue-400', label: 'Approved' },
    resuming: { color: 'bg-cyan-500/10 text-cyan-400', label: 'Resuming' },
    downloading: { color: 'bg-amber-500/10 text-amber-400', label: 'Downloading' },
    installing: { color: 'bg-violet-500/10 text-violet-400', label: 'Installing' },
    completed: { color: 'bg-green-500/10 text-green-400', label: 'Completed' },
    failed: { color: 'bg-red-500/10 text-red-400', label: 'Failed' },
  }
  const item = config[status] ?? { color: 'bg-slate-500/10 text-slate-300', label: status }

  return (
    <span className={clsx('px-2 py-1 text-xs font-medium rounded', item.color)}>
      {item.label}
    </span>
  )
}

export default function DeviceDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { operator } = useAuth()

  const [showCreateTunnel, setShowCreateTunnel] = useState(false)
  const [tunnelForm, setTunnelForm] = useState({
    name: '',
    mode: 'STCP',
    target_port: 22,
    target_addr: '127.0.0.1',
    relay_id: '', // empty means default relay
  })

  const { data: deviceData, isLoading: deviceLoading } = useQuery({
    queryKey: ['device', id],
    queryFn: () => devicesApi.get(id!),
  })

  const { data: installationData } = useQuery({
    queryKey: ['device-installation', id],
    queryFn: () => devicesApi.getInstallation(id!),
    enabled: Boolean(id),
    refetchInterval: 3000,
  })

  const { data: tunnelsData, isLoading: tunnelsLoading } = useQuery({
    queryKey: ['tunnels', id],
    queryFn: () => tunnelsApi.list(id),
    refetchInterval: 5000,
  })

  // Fetch relays for admin users
  const { data: relaysData } = useQuery({
    queryKey: ['relays'],
    queryFn: () => relaysApi.list(),
    enabled: operator?.role === 'admin',
  })

  const createTunnelMutation = useMutation({
    mutationFn: (data: Parameters<typeof tunnelsApi.create>[0]) =>
      tunnelsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tunnels', id] })
      setShowCreateTunnel(false)
      setTunnelForm({ name: '', mode: 'STCP', target_port: 22, target_addr: '127.0.0.1', relay_id: '' })
    },
  })

  const relays: Relay[] = relaysData?.relays || []

  const enableTunnelMutation = useMutation({
    mutationFn: tunnelsApi.enable,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tunnels', id] }),
  })

  const disableTunnelMutation = useMutation({
    mutationFn: tunnelsApi.disable,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tunnels', id] }),
  })

  const deleteTunnelMutation = useMutation({
    mutationFn: tunnelsApi.delete,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tunnels', id] }),
  })

  const device: Device | null = deviceData?.device ?? null
  const installation: DeviceInstallation | null = installationData?.installation ?? null
  const tunnels: Tunnel[] = tunnelsData?.tunnels ?? []

  if (deviceLoading) {
    return <div className="animate-pulse h-64 bg-slate-800 rounded-lg" />
  }

  if (!device) {
    return (
      <div className="card text-center py-12">
        <p className="text-slate-400">Device not found</p>
      </div>
    )
  }

  return (
    <div>
      <button
        onClick={() => navigate('/devices')}
        className="flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Devices
      </button>

      <div className="card mb-8">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <Server className="w-12 h-12 text-slate-400" />
            <div>
              <h1 className="text-2xl font-bold text-white">
                {device.name || device.hostname || 'Unnamed Device'}
              </h1>
              <p className="text-slate-400">{device.hostname}</p>
            </div>
          </div>
          <div
            className={clsx(
              'px-3 py-1 rounded text-sm font-medium',
              device.status === 'active'
                ? 'bg-green-500/10 text-green-400'
                : device.status === 'pending'
                ? 'bg-yellow-500/10 text-yellow-400'
                : 'bg-red-500/10 text-red-400'
            )}
          >
            {device.status}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 text-sm">
          <div>
            <p className="text-slate-400">Enrolled</p>
            <p className="text-white">
              {device.enrolled_at
                ? formatDistanceToNow(new Date(device.enrolled_at), { addSuffix: true })
                : 'Never'}
            </p>
          </div>
          <div>
            <p className="text-slate-400">Last Seen</p>
            <p className="text-white">
              {device.last_seen
                ? formatDistanceToNow(new Date(device.last_seen), { addSuffix: true })
                : 'Never'}
            </p>
          </div>
          <div>
            <p className="text-slate-400">Connected</p>
            <p className={deviceData?.connected ? 'text-green-400' : 'text-red-400'}>
              {deviceData?.connected ? 'Yes' : 'No'}
            </p>
          </div>
          <div>
            <p className="text-slate-400">Tunnels</p>
            <p className="text-white">{tunnels.length}</p>
          </div>
        </div>
      </div>

      {installation && (
        <div className="card mb-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-white">Installation</h2>
              <p className="mt-1 text-sm text-slate-400">
                {installation.message || 'Installer progress reported by the device.'}
              </p>
            </div>
            <InstallationStatusBadge status={installation.status} />
          </div>

          <div className="mt-4">
            <div className="mb-2 flex items-center justify-between text-sm">
              <span className="text-slate-400">Progress</span>
              <span className="text-white">{installation.progress}%</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-slate-800">
              <div
                className={clsx(
                  'h-full transition-all duration-500',
                  installation.status === 'failed' ? 'bg-red-500' : 'bg-cyan-400'
                )}
                style={{ width: `${Math.max(0, Math.min(100, installation.progress))}%` }}
              />
            </div>
          </div>

          <div className="mt-4 grid gap-4 text-sm md:grid-cols-3">
            <div>
              <p className="text-slate-400">Started</p>
              <p className="text-white">
                {installation.started_at
                  ? formatDistanceToNow(new Date(installation.started_at), { addSuffix: true })
                  : 'Unknown'}
              </p>
            </div>
            <div>
              <p className="text-slate-400">Last Update</p>
              <p className="text-white">
                {installation.updated_at
                  ? formatDistanceToNow(new Date(installation.updated_at), { addSuffix: true })
                  : 'Unknown'}
              </p>
            </div>
            <div>
              <p className="text-slate-400">Completed</p>
              <p className="text-white">
                {installation.completed_at
                  ? formatDistanceToNow(new Date(installation.completed_at), { addSuffix: true })
                  : 'Not yet'}
              </p>
            </div>
          </div>

          <div className="mt-5">
            <p className="mb-2 text-sm font-medium text-white">Recent installer log</p>
            {installation.log ? (
              <pre className="max-h-80 overflow-auto rounded-lg border border-slate-800 bg-slate-950/80 p-4 text-xs leading-5 text-slate-300 whitespace-pre-wrap break-all">
                {installation.log}
              </pre>
            ) : (
              <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-4 text-sm text-slate-400">
                No installer logs received yet.
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-white">Tunnels</h2>
        <button
          onClick={() => setShowCreateTunnel(true)}
          className="btn btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Create Tunnel
        </button>
      </div>

      {showCreateTunnel && (
        <div className="card mb-4">
          <h3 className="text-white font-medium mb-4">Create New Tunnel</h3>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              const data: Parameters<typeof tunnelsApi.create>[0] = {
                device_id: id!,
                name: tunnelForm.name,
                mode: tunnelForm.mode,
                target_addr: tunnelForm.target_addr,
                target_port: tunnelForm.target_port,
              }
              // Only include relay_id if explicitly selected
              if (tunnelForm.relay_id) {
                data.relay_id = tunnelForm.relay_id
              }
              createTunnelMutation.mutate(data)
            }}
            className="space-y-4"
          >
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Name</label>
                <input
                  type="text"
                  className="input"
                  value={tunnelForm.name}
                  onChange={(e) =>
                    setTunnelForm({ ...tunnelForm, name: e.target.value })
                  }
                  placeholder="SSH Access"
                />
              </div>
              <div>
                <label className="label">Mode</label>
                <select
                  className="input"
                  value={tunnelForm.mode}
                  onChange={(e) =>
                    setTunnelForm({ ...tunnelForm, mode: e.target.value })
                  }
                >
                  <option value="STCP">STCP (Secret TCP)</option>
                  <option value="XTCP">XTCP (P2P)</option>
                  <option value="TCP">TCP (Public)</option>
                  <option value="HTTP">HTTP</option>
                  <option value="HTTPS">HTTPS</option>
                </select>
              </div>
              <div>
                <label className="label">Target Address</label>
                <input
                  type="text"
                  className="input"
                  value={tunnelForm.target_addr}
                  onChange={(e) =>
                    setTunnelForm({ ...tunnelForm, target_addr: e.target.value })
                  }
                  placeholder="127.0.0.1"
                />
              </div>
              <div>
                <label className="label">Target Port</label>
                <input
                  type="number"
                  className="input"
                  value={tunnelForm.target_port}
                  onChange={(e) =>
                    setTunnelForm({
                      ...tunnelForm,
                      target_port: parseInt(e.target.value),
                    })
                  }
                  placeholder="22"
                />
              </div>
              {operator?.role === 'admin' && relays.length > 0 && (
                <div className="col-span-2">
                  <label className="label">Relay</label>
                  <select
                    className="input"
                    value={tunnelForm.relay_id}
                    onChange={(e) =>
                      setTunnelForm({ ...tunnelForm, relay_id: e.target.value })
                    }
                  >
                    <option value="">Default Relay</option>
                    {relays
                      .filter(r => r.status === 'active')
                      .map((relay) => (
                        <option key={relay.id} value={relay.id}>
                          {relay.name} ({relay.address}){relay.is_default ? ' - Default' : ''}
                        </option>
                      ))}
                  </select>
                  <p className="text-xs text-slate-500 mt-1">
                    Select which relay server to use for this tunnel
                  </p>
                </div>
              )}
            </div>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setShowCreateTunnel(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={createTunnelMutation.isPending}
              >
                {createTunnelMutation.isPending ? 'Creating...' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      )}

      {tunnelsLoading ? (
        <div className="animate-pulse space-y-4">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="h-20 bg-slate-800 rounded-lg" />
          ))}
        </div>
      ) : tunnels.length === 0 ? (
        <div className="card text-center py-8">
          <p className="text-slate-400">No tunnels configured</p>
        </div>
      ) : (
        <div className="space-y-4">
          {tunnels.map((tunnel) => (
            <div key={tunnel.id} className="card">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-3">
                    <span className="text-white font-medium">
                      {tunnel.name || `${tunnel.mode} Tunnel`}
                    </span>
                    <TunnelStatusBadge status={tunnel.status} />
                    <span className="px-2 py-0.5 text-xs bg-slate-700 rounded text-slate-300">
                      {tunnel.mode}
                    </span>
                  </div>
                  <p className="text-slate-400 text-sm mt-1">
                    {tunnel.target_addr}:{tunnel.target_port}
                  </p>
                  {tunnel.status_message && (
                    <p className="text-red-400 text-sm mt-1">{tunnel.status_message}</p>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {tunnel.enabled ? (
                    <button
                      onClick={() => disableTunnelMutation.mutate(tunnel.id)}
                      className="p-2 text-yellow-400 hover:bg-yellow-500/10 rounded-lg transition-colors"
                      title="Disable tunnel"
                    >
                      <Pause className="w-5 h-5" />
                    </button>
                  ) : (
                    <button
                      onClick={() => enableTunnelMutation.mutate(tunnel.id)}
                      className="p-2 text-green-400 hover:bg-green-500/10 rounded-lg transition-colors"
                      title="Enable tunnel"
                    >
                      <Play className="w-5 h-5" />
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (confirm('Delete this tunnel?')) {
                        deleteTunnelMutation.mutate(tunnel.id)
                      }
                    }}
                    className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                    title="Delete tunnel"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
